// services/api.js
class QueueApiService {
  constructor() {

    this.baseURL = /*'http://localhost:8080' /*||*/ 'https://api.farmoish.tj/queue';
    this.token = localStorage.getItem('authToken') || null;
    this.cachedTerminalInfo = null;
    this.isFetchingTerminal = false;
    this.clientTypeCache = new Map();
  }
  
  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  setToken(token) {
    if (!token) return false;
    this.token = token;
    this.cachedTerminalInfo = null;
    this.clientTypeCache.clear();
    localStorage.setItem('authToken', token);
    return true;
  }

  setClientTypeCache(clientTypeId, clientTypeName) {
    if (clientTypeId && clientTypeName) {
      this.clientTypeCache.set(clientTypeId.toString(), clientTypeName);
    }
  }

  getClientTypeFromCache(clientTypeId) {
    return this.clientTypeCache.get(clientTypeId?.toString());
  }

  updateClientTypeCacheFromQueue(queueData) {
    if (queueData && queueData.clientTypeId && queueData.clientTypeName) {
      this.setClientTypeCache(queueData.clientTypeId, queueData.clientTypeName);
    }
  }
  
  getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const t = this.getToken();
    if (t) {
      headers['terminalToken'] = `${t}`;
    }
    return headers;
  }

  async getTerminalInfo() {
    if (this.cachedTerminalInfo) {
      return this.cachedTerminalInfo;
    }

    if (this.isFetchingTerminal) {
      return new Promise((resolve) => {
        const checkCache = setInterval(() => {
          if (this.cachedTerminalInfo) {
            clearInterval(checkCache);
            resolve(this.cachedTerminalInfo);
          }
        }, 100);
      });
    }

    try {
      this.isFetchingTerminal = true;
      const url = `${this.baseURL}/api/v1/terminal/info`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        console.error('❌ [API] HTTP Error:', response.status, response.statusText);
        return null;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ [API] Server returned non-JSON response:', contentType);
        const text = await response.text();
        console.error('📄 [API] Response preview:', text.substring(0, 300));
        return null;
      }

      const data = await response.json();
      this.cachedTerminalInfo = data;
      return data;
    } catch (error) {
      console.error('❌ [API] Error:', error);
      return null;
    } finally {
      this.isFetchingTerminal = false;
    }
  }

  async getServices() {
    try {
      await this.getTerminalInfo();
      const response = await fetch(`${this.baseURL}/api/v1/service/all`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      // If service/all fails, try using terminal info services as fallback
      if (!response.ok) {
        console.log('⚠️ [API] service/all failed, trying terminal info fallback');
        const terminalInfo = await this.getTerminalInfo();
        if (terminalInfo && terminalInfo.services) {
          return terminalInfo.services;
        }
        
        console.error('❌ [API] getServices HTTP Error:', response.status, response.statusText);
        if (response.status === 401) this.handleUnauthorized();
        return null;
      }
      
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ [API] getServices returned non-JSON:', contentType);
        const text = await response.text();
        console.error('Response body:', text.substring(0, 200));
        return null;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ [API] getServices Error:', error);
      
      // Fallback to terminal info services if API call fails
      try {
        console.log('⚠️ [API] API call failed, trying terminal info fallback');
        const terminalInfo = await this.getTerminalInfo();
        if (terminalInfo && terminalInfo.services) {
          return terminalInfo.services;
        }
      } catch (fallbackError) {
        console.error('❌ [API] Fallback also failed:', fallbackError);
      }
      
      return null;
    }
  }

  async getQueueView(paging = { page: 1, pageSize: 20 }) {
    try {
      const params = new URLSearchParams({
        page: paging.page,
        pageSize: paging.pageSize
      });
      const response = await fetch(`${this.baseURL}/api/v1/queueticket/view?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return await response.json();
    } catch (error) {
      return { called: [], waiting: [] };
    }
  }

  async submitPhoneNumber(phoneNumber, terminalServiceId) {
    const terminalInfo = await this.getTerminalInfo();
    const pikUpPointId = terminalInfo?.pickUpPointId || terminalInfo?.pikUpPointId || 0;

    const payload = {
      phoneNumber,
      terminalServiceId,
      pikUpPointId
    };

    const response = await fetch(`${this.baseURL}/api/v1/queueticket`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error('Failed');
      error.response = { data: errorData };
      throw error;
    }
    
    const data = await response.json();
    
    // Cache clientTypeName if present in response
    if (data && data.clientTypeId && data.clientTypeName) {
      this.updateClientTypeCacheFromQueue(data);
    }
    
    return data;
  }

  async submitAppCode(qrCode, terminalServiceId) {
    const terminalInfo = await this.getTerminalInfo();
    const pikUpPointId = terminalInfo?.pickUpPointId || terminalInfo?.pikUpPointId || 0;

    const payload = {
      qrCode,
      terminalServiceId,
      pikUpPointId
    };

    const response = await fetch(`${this.baseURL}/api/v1/QueueTicket/by-qr-code`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error('Failed');
      error.response = { data: errorData };
      throw error;
    }

    const data = await response.json();

    // Cache clientTypeName if present in response
    if (data && data.clientTypeId && data.clientTypeName) {
      this.updateClientTypeCacheFromQueue(data);
    }

    return data;
  }

  handleUnauthorized() {
    localStorage.removeItem('authToken');
    this.token = null;
    this.cachedTerminalInfo = null;
    this.clientTypeCache.clear(); // Clear client type cache on unauthorized
    window.dispatchEvent(new CustomEvent('showTokenModal'));
  }
}

const apiInstance = new QueueApiService();
export default apiInstance;